import { pipe } from 'fp-ts/lib/function'
import * as api from './api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { matchPath, useLocation, useParams } from 'react-router'
import { throwLeft } from '../../axios-extended'
import { useBill } from '../Bills/queries'
import { useShowError } from '../Notifications'
import {
  createPayment,
  getAssetsByPaymentMethod,
  handleMPGSRedirect,
  submitPaymeePayment,
} from './api'

export const useCurrentInvoice = () => {
  const { invoiceID } = useParams<{ invoiceID: string }>()
  return useBill(parseInt(invoiceID, 10))
}

export const usePaymentSupportedAssets = (unitID?: string) => {
  const showError = useShowError()

  return useQuery(
    ['payment-supported-assets', unitID],
    pipe(getAssetsByPaymentMethod(unitID ?? ''), throwLeft),
    {
      enabled: !!unitID,
      onError: () => {
        showError('getAssetsByMethodError')
      },
    }
  )
}

export const useCurrentSupportedAssets = () => {
  const invoiceQuery = useCurrentInvoice()
  const assetsQuery = usePaymentSupportedAssets(invoiceQuery.data?.unitID)

  if (!invoiceQuery.data) {
    return invoiceQuery
  }

  return assetsQuery
}

export const useExchangeRate = (
  provider: 'SMT' | 'MPGS' | 'PAYPAL' | 'SOBFLOUS' | 'PAYMEE',
  currency: string
) => {
  const assetsQuery = useCurrentSupportedAssets()

  if (!assetsQuery.data) {
    return assetsQuery
  }

  return {
    ...assetsQuery,
    data: assetsQuery.data[provider].find(xr => xr.currency === currency),
  }
}

export const useRefreshInvoice = () => {
  const { invoiceID } = useParams<{ invoiceID: string }>()
  const id = parseInt(invoiceID, 10)
  const queryClient = useQueryClient()

  return () => queryClient.invalidateQueries(['bill', id])
}

export const useCreatePayment = () => {
  const showError = useShowError()
  const invoiceQuery = useCurrentInvoice()
  const supportedAssets = useCurrentSupportedAssets()

  return useMutation(
    'create-payment',
    async ({
      currency,
      method,
    }: {
      currency: string
      method: Exclude<api.PaymentGateway, 'DIRECT'>
    }) => {
      if (!invoiceQuery.data || !supportedAssets.data) {
        return undefined
      }

      const res = await pipe(
        createPayment(method, invoiceQuery.data.id, currency),
        throwLeft
      )()

      switch (res.additionalData.gateway) {
        case 'MPGS': {
          return await handleMPGSRedirect(
            res.order.formUrl,
            invoiceQuery.data.organizationInfo.name,
            res.additionalData.data.merchant,
            {
              id: `${res.order.id}`,
              amount: res.additionalData.data.amount,
              currency,
              description: invoiceQuery.data.reference,
            }
          )()
        }
        case 'PAYMEE': {
          submitPaymeePayment(invoiceQuery.data.id, res.order.formUrl)
          return res
        }
        default: {
          window.location.href = res.order.formUrl
          return res
        }
      }
    },
    {
      onError: e => {
        console.error(e)
        showError('createPaymentError')
      },
    }
  )
}

const useTransmID = () => {
  const location = useLocation()

  const m = matchPath<{ invoiceID: string }>(location.pathname, {
    path: '/payment/sobflous/complete',
    exact: true,
  })
  if (!m) {
    return undefined
  }

  const search = location.search.substr(1)
  const transmID = new URLSearchParams(search).get('transm_id')
  if (!transmID) {
    return undefined
  }

  const orderID = parseInt(transmID, 10)
  if (isNaN(orderID)) {
    return undefined
  }

  return `${orderID}`
}

export const usePaymentStatus = () => {
  const showError = useShowError()
  const transmID = useTransmID()
  const location = useLocation()

  const params = new URLSearchParams(location.search)
  const paymentID =
    params.get('orderId') ?? params.get('payment_token') ?? undefined
  const token = params.get('token') ?? undefined
  const orderID = params.get('id') ?? undefined
  const actualOrderID = params.get('actualOrderID') ?? transmID

  return useQuery(
    ['payment-status', params, paymentID, token, orderID, actualOrderID],
    pipe(
      paymentID
        ? api.checkPayment(paymentID)
        : orderID
        ? api.checkPaymentID(orderID)
        : actualOrderID
        ? api.checkPaymentByOrderID(actualOrderID)
        : api.checkPayment(token ?? ''),
      throwLeft
    ),
    {
      enabled: (paymentID ?? orderID ?? token ?? actualOrderID) !== undefined,
      onError: e => {
        console.error('payment status error', e)
        showError('checkPaymentError')
      },
    }
  )
}
